import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  Theme,
  Tooltip,
} from '@material-ui/core';
import { Error } from '@material-ui/icons';
import { formatCurrency } from '@roc/ui/utils';
import { YesNoChip } from '@roc/ui';
import { formatDateIgnoreTimezone } from '@roc/feature-utils';

const useStyles = makeStyles((theme: Theme) => ({
  titleCell: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
    fontWeight: 'bold',
  },
  labelCell: {
    width: '20%',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
  },
  valueCell: {
    width: '37.5%',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
  },
  matchCell: {
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: '1px solid #E0E0E0',
    padding: theme.spacing(1.5, 2),
    textAlign: 'center',
    width: '5%',
  },
  lastRow: {
    '& td': {
      borderBottom: 'none',
    },
  },
}));

type FieldType = 'currency' | 'date' | 'boolean' | 'text';

interface FieldRow {
  label: string;
  extractedKey: string;
  expectedKey?: string;
  matchKey?: string;
  referencedKey?: string;
  type: FieldType;
}

const UCC_SUBTYPES = ['RECORDED_UCC1_STATE', 'RECORDED_UCC1_COUNTY'];

const FIELD_ROWS: FieldRow[] = [
  {
    label: 'Loan Amount',
    extractedKey: 'extractedLoanAmount',
    expectedKey: 'portalLoanAmount',
    matchKey: 'loanAmountsMatch',
    type: 'currency',
  },
  {
    label: 'Closing Date',
    extractedKey: 'extractedDate',
    expectedKey: 'portalClosingDate',
    matchKey: 'closingDatesMatch',
    type: 'date',
  },
  {
    label: 'Property Address',
    extractedKey: 'extractedAddress',
    expectedKey: 'portalAddress',
    matchKey: 'propertyAddressesMatch',
    type: 'text',
  },
  {
    label: 'Entity Name',
    extractedKey: 'extractedEntityName',
    expectedKey: 'portalEntityName',
    matchKey: 'entityNamesMatch',
    type: 'text',
  },
  {
    label: 'Lender Name',
    extractedKey: 'extractedLenderName',
    expectedKey: 'portalLenderName',
    matchKey: 'lenderNamesMatch',
    type: 'text',
  },
  { label: 'Did Borrower Sign', extractedKey: 'didBorrowerSign', type: 'boolean' },
  { label: 'Did Notary Sign', extractedKey: 'didNotarySign', type: 'boolean' },
  { label: 'Notary Stamp Present', extractedKey: 'notaryStampPresent', type: 'boolean' },
  { label: 'Proof of Recording Present', extractedKey: 'proofOfRecordingPresent', type: 'boolean' },
  { label: 'Legal Description Present', extractedKey: 'legalDescriptionPresent', type: 'boolean' },
  { label: 'Post Closer', extractedKey: 'postCloserName', type: 'text' },
];

const UCC_FIELD_ROWS: FieldRow[] = [
  {
    label: 'Recording Date',
    extractedKey: 'extractedDate',
    expectedKey: 'portalClosingDate',
    matchKey: 'closingDatesMatch',
    type: 'date',
  },
  { label: 'Recording Information', extractedKey: 'recordingInformation', type: 'text' },
  {
    label: 'Property Address',
    extractedKey: 'extractedAddress',
    expectedKey: 'portalAddress',
    matchKey: 'propertyAddressesMatch',
    type: 'text',
  },
  {
    label: 'Debtor Organization Name',
    extractedKey: 'extractedDebtorOrganizationName',
    expectedKey: 'portalEntityName',
    matchKey: 'entityNamesMatch',
    type: 'text',
  },
  {
    label: 'Debtor Individual Name',
    extractedKey: 'extractedDebtorIndividualName',
    expectedKey: 'portalEntityName',
    matchKey: 'entityNamesMatch',
    type: 'text',
  },
  {
    label: 'Secured Party Name',
    extractedKey: 'extractedLenderName',
    expectedKey: 'portalLenderName',
    matchKey: 'lenderNamesMatch',
    type: 'text',
  },
  { label: 'Schedule A', extractedKey: 'scheduleA', referencedKey: 'pointFourScheduleA', type: 'text' },
  { label: 'Exhibit A', extractedKey: 'exhibitA', referencedKey: 'pointFourExhibitA', type: 'text' },
  { label: 'Exhibit B', extractedKey: 'exhibitB', referencedKey: 'pointFourExhibitB', type: 'text' },
  { label: 'Exhibit C', extractedKey: 'exhibitC', referencedKey: 'pointFourExhibitC', type: 'text' },
  { label: 'Legal Description', extractedKey: 'legalDescription', type: 'text' },
  { label: 'Post Closer', extractedKey: 'postCloserName', type: 'text' },
];

const isNullish = (value: any) => value === null || value === undefined;

// A building loan mortgage is compared against the construction holdback, not the
// loan amount (see PortalValues#loanAmountFor on the backend).
const labelFor = (row: FieldRow, subtype?: string) =>
  subtype === 'RECORDED_BUILDING_MORTGAGE' && row.extractedKey === 'extractedLoanAmount'
    ? 'Construction Holdback'
    : row.label;

const formatCell = (type: FieldType, value: any) => {
  if (isNullish(value)) return '—';
  if (type === 'boolean') return <YesNoChip value={value ? 'Yes' : 'No'} />;
  if (type === 'currency') return formatCurrency(Number(value));
  if (type === 'date') return formatDateIgnoreTimezone(value);
  return String(value);
};

export interface RecordedDocumentReviewTableProps {
  data: Record<string, any>;
}

export const RecordedDocumentReviewTable = ({ data }: RecordedDocumentReviewTableProps) => {
  const classes = useStyles();
  const fieldRows = UCC_SUBTYPES.includes(data?.subtype) ? UCC_FIELD_ROWS : FIELD_ROWS;
  const rows = fieldRows
    .map(row => {
      const isReferenced = row.referencedKey ? data[row.referencedKey] === true : true;
      const rawExtractedValue = data[row.extractedKey];
      const isMissingReferencedItem =
        !!row.referencedKey && isReferenced && isNullish(rawExtractedValue);
      return {
        ...row,
        label: labelFor(row, data?.subtype),
        extractedValue: isReferenced ? rawExtractedValue : null,
        expectedValue: row.expectedKey ? data[row.expectedKey] : undefined,
        isMismatch: (!!row.matchKey && data[row.matchKey] === false) || isMissingReferencedItem,
        mismatchTooltip: isMissingReferencedItem
          ? 'Item 4 references this item, but it could not be extracted from the document.'
          : 'The extracted value does not match the portal value.',
      };
    })
    .filter(row => row.isMismatch || !(isNullish(row.extractedValue) && isNullish(row.expectedValue)));

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell className={classes.matchCell} />
          <TableCell className={classes.titleCell}>Field</TableCell>
          <TableCell className={classes.titleCell}>Portal</TableCell>
          <TableCell className={classes.titleCell}>Document</TableCell>
        </TableRow>
        {rows.map((row, index) => (
          <TableRow key={row.extractedKey} className={index === rows.length - 1 ? classes.lastRow : ''}>
            <TableCell className={classes.matchCell}>
              {row.isMismatch && (
                <Tooltip title={row.mismatchTooltip}>
                  <Error color="error" />
                </Tooltip>
              )}
            </TableCell>
            <TableCell className={classes.labelCell}>{row.label}</TableCell>
            <TableCell className={classes.valueCell}>
              {row.expectedKey ? formatCell(row.type, row.expectedValue) : '—'}
            </TableCell>
            <TableCell className={classes.valueCell}>{formatCell(row.type, row.extractedValue)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
